import { createSignal, onMount, For, Show } from 'solid-js';
import { useActiveBot } from '../../state/activeBot';
import { useBotFanOut } from '../../hooks/useBotFanOut';
import { BOT_ACTIONS } from '../../services/resourceActions';
import ItemIdentifierPicker from '../../components/ItemIdentifierPicker';
import ItemStackView from '../../components/ItemStackView';
import { fromIdentifier } from '../../services/itemRefAdapters';
import { useCraftingResolver } from '../../services/craftingResolverContext';
import { useApi, isOk } from '../../api';
import { showConfirm } from '../../components/Dialog';
import { useToast } from '../../components/MessageBox';
import { useInventoryApi, type StorageInfo } from '../../services/inventoryApi';
import { Citybuild } from '../../models/Citybuild';
import Text from '../../components/Text';
import LocationPicker from '../../components/LocationPicker';
import AreaInput from '../../components/AreaInput';
import type { Coordinate } from '../../components/CoordinateInput';
import { useTranslation } from '../../hooks/useTranslation';
import BotScoped from '../../components/BotScoped';
import StorageSelect from '../../components/StorageSelect';
import styles from './Demolish.module.css';
import { firstError } from '../../api/envelope';

const citybuildOptions = Object.keys(Citybuild).filter(k => isNaN(Number(k))) as (keyof typeof Citybuild)[];

const FILTER_MODE_DISABLED = 0;
const FILTER_MODE_WHITELIST = 1;
const FILTER_MODE_BLACKLIST = 2;

interface DemolishConfig {
    id: string;
    name: string;
    citybuild: number;
    citybuildName: string;
    location: string;
    x1: number;
    y1: number;
    z1: number;
    x2: number;
    y2: number;
    z2: number;
    blockIdentifiers: string[];
    filterMode: number;
    unloadStorageId: string;
    everythingExceptRepairable: boolean;
    itemIdentifierFilter: string[];
    minFreeSlots: number;
    compress: boolean;
    allowScaffold: boolean;
    preserveBlocks: boolean;
    isEnabled: boolean;
    createdAt: string;
}

export default function DemolishPage() {
    return <BotScoped groupAware><DemolishInner /></BotScoped>;
}

function DemolishInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const resolver = useCraftingResolver();

    const [configs, setConfigs] = createSignal<DemolishConfig[]>([]);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);

    const [showForm, setShowForm] = createSignal(false);
    const [editId, setEditId] = createSignal<string | null>(null);
    const [formName, setFormName] = createSignal('');
    const [formCitybuild, setFormCitybuild] = createSignal(0);
    const [formLocation, setFormLocation] = createSignal('');
    const [formCorner1, setFormCorner1] = createSignal<Coordinate>({ x: 0, y: 0, z: 0 });
    const [formCorner2, setFormCorner2] = createSignal<Coordinate>({ x: 0, y: 0, z: 0 });
    const [formBlockIdentifiers, setFormBlockIdentifiers] = createSignal<string[]>([]);
    const [formFilterMode, setFormFilterMode] = createSignal(FILTER_MODE_DISABLED);
    const [formStorageId, setFormStorageId] = createSignal('');
    const [formEverything, setFormEverything] = createSignal(true);
    const [formItemIdentifierFilter, setFormItemIdentifierFilter] = createSignal<string[]>([]);
    const [formMinFreeSlots, setFormMinFreeSlots] = createSignal(4);
    const [formCompress, setFormCompress] = createSignal(false);
    const [formAllowScaffold, setFormAllowScaffold] = createSignal(false);
    const [formPreserveBlocks, setFormPreserveBlocks] = createSignal(false);
    const [formIsEnabled, setFormIsEnabled] = createSignal(true);
    const [saving, setSaving] = createSignal(false);

    const { botId: activeBotId, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const botId = () => activeBotId() ?? '';
    const inv = useInventoryApi();

    onMount(async () => {
        try {
            const [cfgRes, storageList] = await Promise.all([
                api.demolish.getDemolishConfigs(),
                inv.fetchStorages(),
            ]);
            if (isOk(cfgRes.data)) setConfigs((cfgRes.data.value.configs ?? []) as DemolishConfig[]);
            setStorages(storageList);
        } catch { /* toast below */ }
        finally { setLoading(false); }
    });

    async function loadConfigs() {
        const res = await api.demolish.getDemolishConfigs();
        if (isOk(res.data)) setConfigs((res.data.value.configs ?? []) as DemolishConfig[]);
    }

    function openCreate() {
        setEditId(null);
        setFormName('');
        setFormCitybuild(0);
        setFormLocation('');
        setFormCorner1({ x: 0, y: 0, z: 0 });
        setFormCorner2({ x: 0, y: 0, z: 0 });
        setFormBlockIdentifiers([]);
        setFormFilterMode(FILTER_MODE_DISABLED);
        setFormStorageId(storages()[0]?.id ?? '');
        setFormEverything(true);
        setFormItemIdentifierFilter([]);
        setFormMinFreeSlots(4);
        setFormCompress(false);
        setFormAllowScaffold(false);
        setFormPreserveBlocks(false);
        setFormIsEnabled(true);
        setShowForm(true);
    }

    function openEdit(c: DemolishConfig) {
        setEditId(c.id);
        setFormName(c.name);
        setFormCitybuild(c.citybuild);
        setFormLocation(c.location);
        setFormCorner1({ x: c.x1, y: c.y1, z: c.z1 });
        setFormCorner2({ x: c.x2, y: c.y2, z: c.z2 });
        setFormBlockIdentifiers(c.blockIdentifiers ?? []);
        setFormFilterMode(c.filterMode);
        setFormStorageId(c.unloadStorageId);
        setFormEverything(c.everythingExceptRepairable);
        setFormItemIdentifierFilter(c.itemIdentifierFilter ?? []);
        setFormMinFreeSlots(c.minFreeSlots);
        setFormCompress(c.compress);
        setFormAllowScaffold(c.allowScaffold);
        setFormPreserveBlocks(c.preserveBlocks);
        setFormIsEnabled(c.isEnabled);
        setShowForm(true);
    }

    async function save(e: Event) {
        e.preventDefault();
        if (!formStorageId()) { toast?.error(t('DEMOLISH_SELECT_STORAGE')); return; }
        setSaving(true);
        const body = {
            name: formName().trim() || 'Demolish Config',
            citybuild: formCitybuild(),
            location: formLocation().trim(),
            x1: formCorner1().x, y1: formCorner1().y, z1: formCorner1().z,
            x2: formCorner2().x, y2: formCorner2().y, z2: formCorner2().z,
            blockIdentifiers: formFilterMode() === FILTER_MODE_DISABLED ? [] : formBlockIdentifiers(),
            filterMode: formFilterMode(),
            unloadStorageId: formStorageId(),
            everythingExceptRepairable: formEverything(),
            itemIdentifierFilter: formEverything() ? [] : formItemIdentifierFilter(),
            minFreeSlots: formMinFreeSlots(),
            compress: formCompress(),
            allowScaffold: formAllowScaffold(),
            preserveBlocks: formPreserveBlocks(),
            isEnabled: formIsEnabled(),
        };
        try {
            const id = editId();
            const res = id
                ? await api.demolish.updateDemolishConfig(id, body)
                : await api.demolish.createDemolishConfig(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('DEMOLISH_SAVE_FAILED'));
                return;
            }
            setShowForm(false);
            toast?.info(id ? t('DEMOLISH_UPDATED') : t('DEMOLISH_CREATED'));
            await loadConfigs();
        } finally { setSaving(false); }
    }

    async function deleteConfig(id: string) {
        const confirmed = await showConfirm(t('DEMOLISH_DELETE_CONFIRM'), true);
        if (!confirmed) return;
        const res = await api.demolish.deleteDemolishConfig(id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('DEMOLISH_DELETE_FAILED'));
            return;
        }
        toast?.info(t('DEMOLISH_DELETED'));
        await loadConfigs();
    }

    async function runConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('DEMOLISH_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.demolish.runDemolishConfig(id, { botId: targetBotId }),
            { success: t('DEMOLISH_STARTED'), failure: t('DEMOLISH_START_FAILED') });
    }

    async function stopConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('DEMOLISH_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.demolish.stopDemolish(id, { botId: targetBotId }),
            { success: t('DEMOLISH_STOPPED'), failure: t('DEMOLISH_STOP_FAILED') });
    }

    function storageName(id: string) {
        return storages().find(s => s.id === id)?.name ?? id.slice(0, 8) + '…';
    }

    function filterModeLabel(mode: number) {
        if (mode === FILTER_MODE_WHITELIST) return t('DEMOLISH_FILTER_WHITELIST');
        if (mode === FILTER_MODE_BLACKLIST) return t('DEMOLISH_FILTER_BLACKLIST');
        return t('DEMOLISH_FILTER_DISABLED');
    }

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="DEMOLISH_TITLE"/></h1>
                <button class="btn btn-primary" onClick={openCreate}>
                    <i class="fa-solid fa-plus" /> <Text val="DEMOLISH_NEW_CONFIG"/>
                </button>
            </div>
            <p class={styles.subtitle}><Text val="DEMOLISH_SUBTITLE"/></p>

            <Show when={loading()}>
                <div class={styles.empty}>
                    <i class="fa-solid fa-spinner fa-spin fa-2x" />
                </div>
            </Show>

            <Show when={!loading()}>
                <Show when={showForm()}>
                    <form class={`fcard ${styles.form}`} onSubmit={save}>
                        <h2 class={styles.formTitle}>
                            {editId() ? <Text val="DEMOLISH_EDIT"/> : <Text val="DEMOLISH_CREATE"/>}
                        </h2>

                        <div class={styles.formGrid}>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="DEMOLISH_F_NAME"/></span>
                                <input class={styles.input} value={formName()} onInput={e => setFormName(e.currentTarget.value)} placeholder={t('DEMOLISH_NAME_PLACEHOLDER')} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="DEMOLISH_F_CITYBUILD"/></span>
                                <select class={styles.input} value={formCitybuild()} onChange={e => setFormCitybuild(parseInt(e.currentTarget.value))}>
                                    <For each={citybuildOptions}>{(cb) => <option value={Citybuild[cb]}>{cb}</option>}</For>
                                </select>
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="DEMOLISH_F_LOCATION"/></span>
                                <LocationPicker value={formLocation()} onChange={setFormLocation} optional />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="DEMOLISH_F_STORAGE"/></span>
                                <StorageSelect
                                    storages={storages()}
                                    value={formStorageId()}
                                    onChange={setFormStorageId}
                                    placeholder={t('DEMOLISH_SELECT_STORAGE_OPTION')}
                                />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="DEMOLISH_F_MINFREE"/></span>
                                <input class={styles.input} type="number" min="1" max="27" value={formMinFreeSlots()} onInput={e => setFormMinFreeSlots(parseInt(e.currentTarget.value) || 4)} />
                            </label>
                        </div>

                        <AreaInput
                            corner1={formCorner1()}
                            corner2={formCorner2()}
                            onChange={(corner1, corner2) => { setFormCorner1(corner1); setFormCorner2(corner2); }}
                            fieldId="demolish.area"
                            label={t('DEMOLISH_F_AREA')}
                            citybuild={formCitybuild()}
                            allowedBots={botId() ? [botId()] : []} />

                        <label class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="DEMOLISH_F_FILTER_MODE"/></span>
                            <select class={styles.input} value={formFilterMode()} onChange={e => setFormFilterMode(parseInt(e.currentTarget.value))}>
                                <option value={FILTER_MODE_DISABLED}>{t('DEMOLISH_FILTER_DISABLED')}</option>
                                <option value={FILTER_MODE_WHITELIST}>{t('DEMOLISH_FILTER_WHITELIST')}</option>
                                <option value={FILTER_MODE_BLACKLIST}>{t('DEMOLISH_FILTER_BLACKLIST')}</option>
                            </select>
                        </label>

                        <Show when={formFilterMode() !== FILTER_MODE_DISABLED}>
                            <div class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="DEMOLISH_F_BLOCKS"/></span>
                                <ItemIdentifierPicker
                                    value={formBlockIdentifiers()}
                                    onChange={setFormBlockIdentifiers}
                                    filter={(d) => d.id <= 255}
                                    placeholder={t('DEMOLISH_BLOCKS_PLACEHOLDER')}
                                    label="blocks"
                                    metaAware
                                    disabled={saving()}
                                />
                            </div>
                        </Show>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formEverything()} onChange={e => setFormEverything(e.currentTarget.checked)} />
                            <span><Text val="DEMOLISH_F_EVERYTHING"/></span>
                        </label>

                        <Show when={!formEverything()}>
                            <div class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="DEMOLISH_F_FILTER"/></span>
                                <ItemIdentifierPicker
                                    value={formItemIdentifierFilter()}
                                    onChange={setFormItemIdentifierFilter}
                                    placeholder={t('DEMOLISH_FILTER_PLACEHOLDER')}
                                    label="items"
                                    disabled={saving()}
                                />
                            </div>
                        </Show>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formCompress()} onChange={e => setFormCompress(e.currentTarget.checked)} />
                            <span><Text val="DEMOLISH_F_COMPRESS"/></span>
                        </label>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formAllowScaffold()} onChange={e => setFormAllowScaffold(e.currentTarget.checked)} />
                            <span><Text val="DEMOLISH_F_SCAFFOLD"/></span>
                        </label>
                        <span class={styles.fieldHint}><Text val="DEMOLISH_F_SCAFFOLD_HINT"/></span>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formPreserveBlocks()} onChange={e => setFormPreserveBlocks(e.currentTarget.checked)} />
                            <span><Text val="DEMOLISH_F_PRESERVE"/></span>
                        </label>
                        <span class={styles.fieldHint}><Text val="DEMOLISH_F_PRESERVE_HINT"/></span>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formIsEnabled()} onChange={e => setFormIsEnabled(e.currentTarget.checked)} />
                            <span><Text val="DEMOLISH_F_ENABLED"/></span>
                        </label>

                        <div class={styles.formActions}>
                            <button type="button" class="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving()}>
                                <Text val="CANCEL"/>
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={saving() || !formStorageId()}>
                                <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE"/></>}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="SAVE"/>
                                </Show>
                            </button>
                        </div>
                    </form>
                </Show>

                <Show when={configs().length === 0 && !showForm()}>
                    <div class={styles.empty}>
                        <i class="fa-solid fa-explosion" />
                        <p><Text val="DEMOLISH_EMPTY"/></p>
                    </div>
                </Show>

                <div class={styles.grid}>
                    <For each={configs()}>
                        {(c) => (
                            <div class={`fcard ${styles.card} ${!c.isEnabled ? styles.cardDisabled : ''}`}>
                                <div class={styles.cardHeader}>
                                    <div>
                                        <div class={styles.cardName}>{c.name}</div>
                                        <div class={styles.cardMeta}>
                                            {c.citybuildName}
                                            {c.location ? ` · ${c.location}` : ''}
                                        </div>
                                    </div>
                                    <span class={c.isEnabled ? styles.pillEnabled : styles.pillDisabled}>
                                        {c.isEnabled ? <Text val="DEMOLISH_ENABLED"/> : <Text val="DEMOLISH_DISABLED"/>}
                                    </span>
                                </div>

                                <Show when={c.filterMode !== FILTER_MODE_DISABLED}>
                                    <div class={styles.blockChips}>
                                        <Show when={(c.blockIdentifiers?.length ?? 0) > 0} fallback={
                                            <span class={styles.noBlocks}><Text val="DEMOLISH_NO_BLOCKS"/></span>
                                        }>
                                            <For each={c.blockIdentifiers}>
                                                {(id) => (
                                                    <div class={styles.blockChip} title={id}>
                                                        <ItemStackView stack={fromIdentifier(id, 0, resolver())} size={24} bare />
                                                    </div>
                                                )}
                                            </For>
                                        </Show>
                                    </div>
                                </Show>

                                <div class={styles.cardBody}>
                                    <div class={styles.infoRow}>
                                        <i class="fa-solid fa-cube" />
                                        ({c.x1}, {c.y1}, {c.z1}) → ({c.x2}, {c.y2}, {c.z2})
                                    </div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-filter" /> {filterModeLabel(c.filterMode)}</div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-warehouse" /> {storageName(c.unloadStorageId)}</div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-box-open" /> {t('DEMOLISH_INFO_MINFREE', String(c.minFreeSlots))}</div>
                                    <div class={styles.infoRow}>
                                        <i class="fa-solid fa-boxes-stacked" />
                                        {c.everythingExceptRepairable
                                            ? t('DEMOLISH_INFO_ALL_NONREPAIRABLE')
                                            : t('DEMOLISH_INFO_ITEM_COUNT', String(c.itemIdentifierFilter?.length ?? 0))}
                                    </div>
                                    <div class={styles.infoRow}>
                                        <i class="fa-solid fa-layer-group" />
                                        {c.allowScaffold
                                            ? t('DEMOLISH_INFO_SCAFFOLD_ON')
                                            : t('DEMOLISH_INFO_SCAFFOLD_OFF')}
                                    </div>
                                    <Show when={c.preserveBlocks}>
                                        <div class={styles.infoRow}><i class="fa-solid fa-gem" /> <Text val="DEMOLISH_INFO_PRESERVE"/></div>
                                    </Show>
                                </div>

                                <div class={styles.cardActions}>
                                    <button class="btn btn-primary" onClick={() => runConfig(c.id)} disabled={!botId()} title={t('RUN')}>
                                        <i class="fa-solid fa-play" /> <Text val="DEMOLISH_RUN"/>
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => stopConfig(c.id)} disabled={!botId()} title={t('STOP')}>
                                        <i class="fa-solid fa-stop" />
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => openEdit(c)} title={t('EDIT')}>
                                        <i class="fa-solid fa-pen" />
                                    </button>
                                    <button class="btn btn-danger" onClick={() => deleteConfig(c.id)} title={t('DELETE')}>
                                        <i class="fa-solid fa-trash" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </For>
                </div>
            </Show>
        </div>
    );
}
